import { S25Util } from "../../../util/s25-util";
import { S25DateParse } from "../../../util/core/s25.date.parse";
import { OrgInvoiceRecords } from "./org-invoice-records";

export namespace OrgInvoiceMetrics {
    export function balance(record: OrgInvoiceRecords.Record): number {
        return Math.max(0, record.totalCharge - record.paidAmount);
    }

    export function daysPastDue(record: OrgInvoiceRecords.Record, now: Date): number {
        if (!record.unpaidDueDateISO) return 0;
        return S25Util.date.diffDays(S25DateParse.parseDropTZLenient(record.unpaidDueDateISO), now);
    }

    export function isOverdue(record: OrgInvoiceRecords.Record, now: Date): boolean {
        return balance(record) > 0 && daysPastDue(record, now) > 0;
    }

    export function status(record: OrgInvoiceRecords.Record, now: Date): OrgInvoiceRecords.Status {
        if (balance(record) <= 0) return "paid";
        return isOverdue(record, now) ? "overdue" : "outstanding";
    }

    export function daysLate(record: OrgInvoiceRecords.Record, now: Date): number {
        return isOverdue(record, now) ? daysPastDue(record, now) : 0;
    }

    export type Tiles = {
        totalBalance: number;
        overdueBalance: number;
        openInvoices: number;
        oldestUnpaidDays: number;
    };

    export function tiles(records: OrgInvoiceRecords.Record[], now: Date): Tiles {
        return {
            totalBalance: records.reduce((sum, r) => sum + balance(r), 0),
            overdueBalance: records.reduce((sum, r) => sum + (isOverdue(r, now) ? balance(r) : 0), 0),
            openInvoices: records.filter((r) => balance(r) > 0).length,
            oldestUnpaidDays: Math.max(0, ...records.map((r) => daysLate(r, now))),
        };
    }

    export type CollectionSplit = { total: number; paid: number; current: number; overdue: number };

    /**
     * Splits what the org has been billed into paid, current and overdue, for the collect-bar chart.
     * Overdue is capped at what's left to collect, same as `AccountSummaryMetrics.collectionProgressSplit`,
     * so an over-paid record (paidAmount > totalCharge on one invoice) can't push the three segments past
     * 100% between them.
     */
    export function collectionSplit(records: OrgInvoiceRecords.Record[], now: Date): CollectionSplit {
        const total = records.reduce((sum, r) => sum + r.totalCharge, 0),
            paid = records.reduce((sum, r) => sum + r.paidAmount, 0),
            rawOverdue = records.reduce((sum, r) => sum + (isOverdue(r, now) ? balance(r) : 0), 0),
            overdue = Math.min(rawOverdue, Math.max(0, total - paid));

        return { total, paid, overdue, current: Math.max(0, total - paid - overdue) };
    }

    /** A percentage clamped to 0-100, safe for use as a CSS width. */
    export function percentOf(part: number, whole: number): number {
        if (!whole) return 0;
        return Math.max(0, Math.min(100, (part / whole) * 100));
    }

    export type AgingBucket = { label: string; amount: number; overdue: boolean };

    /** Outstanding balance bucketed by how many days past due it is. */
    export function agingBuckets(records: OrgInvoiceRecords.Record[], now: Date): AgingBucket[] {
        const buckets: AgingBucket[] = [
            { label: "Current", amount: 0, overdue: false },
            { label: "1–30 days", amount: 0, overdue: true },
            { label: "31–60 days", amount: 0, overdue: true },
            { label: "61–90 days", amount: 0, overdue: true },
            { label: "91+ days", amount: 0, overdue: true },
        ];

        for (const record of records) {
            const amount = balance(record);
            if (amount <= 0) continue;
            const late = daysLate(record, now);
            const index = late <= 0 ? 0 : late <= 30 ? 1 : late <= 60 ? 2 : late <= 90 ? 3 : 4;
            buckets[index].amount += amount;
        }
        return buckets;
    }
}
